import React from 'react';
import { Box, Button, createStyles, Dialog, DialogActions, DialogContent, Grid, makeStyles, ModalProps, Paper, TextField, Theme, Typography } from "@material-ui/core";
import { Field, FieldInputProps, Form } from 'react-final-form';
import { KeyboardDatePicker } from '@material-ui/pickers/DatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment from 'moment';
import { getDurationType } from '../../helpers/commonHelper';
import { CurrencyInput } from '../../components/CurrencyInput';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { formatDate, formatNumber } from '../../helpers/ChartHelper';
import { RvLoader } from '../../components/Loader';
import { client } from '../..';
import { showNotification } from '../../App';
import { FormApi } from 'final-form';
import clsx from 'clsx';

interface ForecastParametersDialogProps extends Pick<ModalProps, 'open' | 'onClose'>, Pick<MatterForecastAutomationDataVariables, 'matterId'>{
    matterGuid?: string;
    onSuccess?: () => void;
    title: string;
    description: string;
}

interface ForecastParametersFormValues {
    startDate: Date | null;
    endDate: Date | null;
    duration?: string;
    feesToBeBilled: number | null;
    estimatedFees: number | null;
    debtorsDays: number | null;
    chargeRate: number | null;
    expectedDaysRemaining: number | null;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {},
        textField: {
            width: '100%',
        },
        paper: {
            padding: theme.spacing(2),
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        actionsWrapper: {
            display: 'flex',
            flexFlow: 'row wrap',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            gap: '30px',
            padding: '20px 0px',
        },
        loadingWrapper: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%'
        },
        rightInput: {
            '& input': {
                textAlign: 'right'
            }
        }
    })
);

export function ForecastParametersDialog(props: ForecastParametersDialogProps) {
    
    const classes = useStyles();
    
    const currencyQuery = useCurrencySymbol();
    
    const matterForecastAutomationDataQuery = useQuery<MatterForecastAutomationData, MatterForecastAutomationDataVariables>(
        MatterForecastAutomationDataQuery,
        {
            variables: {
                matterId: props.matterId || 263,
            },
            fetchPolicy: 'no-cache',
        }
    );
            
    const { 
        ...rest 
    } = props;
    
        
    if (currencyQuery.loading || matterForecastAutomationDataQuery.loading) {
        return (
            <Dialog 
                {...rest}
                fullWidth={true}
                maxWidth="md"
            >
                <DialogContent>
                    <div
                        className={classes.loadingWrapper}
                    >
                        <RvLoader />
                    </div>
                </DialogContent>
            </Dialog>
        );
    }
    
    const forecastAutomations = matterForecastAutomationDataQuery.data?.matterForecastAutomationList.forecastAutomations;
    
    const initialValues: ForecastParametersFormValues = {
        startDate: forecastAutomations?.forecastStart || null,
        endDate: forecastAutomations?.forecastEnd || null,
        duration: forecastAutomations?.forecastDuration || '',
        feesToBeBilled: forecastAutomations?.forecastFeesToBeBilled || null,
        estimatedFees: forecastAutomations?.estimatedFees || null,
        debtorsDays: forecastAutomations?.debtorsDays || null,
        chargeRate: forecastAutomations?.averageChargeRate || null,
        // forecast start and end date to be used to calculate expected days remaining calculate manually for now forecastStart - forecastEnd because we dont have expectedDaysRemaining
        expectedDaysRemaining: forecastAutomations && forecastAutomations.forecastStart && forecastAutomations.forecastEnd
            ? moment(forecastAutomations.forecastEnd).diff(moment(forecastAutomations.forecastStart), 'days')
            : null
    };
    

    const required = (value: any) => {
        return value ? undefined : 'Required';
    };
    
    const onSubmit = (values: ForecastParametersFormValues) => {
        // code here
        
        client
            .mutate<UpdateMatterForecastMutationData, UpdateMatterForecastMutationVariables>({
                mutation: UpdateMatterForecastMutation,
                variables: {
                    input: {
                        matterGuid: props.matterGuid,
                        debtorsDays: values.debtorsDays || undefined,
                        forecastEndDate: values.endDate || undefined,
                        forecastStartDate: values.startDate || undefined,
                        feesBilled: values.feesToBeBilled || undefined,
                        chargeRate: values.chargeRate || undefined
                    }
                }
            })
            // tslint:disable-next-line: no-any
            .then((results) => {
                if (results.data?.matter.matterForecast.matterAutomation.status && !results.data.matter.matterForecast.matterAutomation.error) {
                    // showNotification(null, 'Matter Forecast Updated Successfully', 'info');
                    props.onSuccess?.();
                    props.onClose?.({}, 'backdropClick');
                } else {
                    showNotification('Failed to Update Matter Forecast', results.data?.matter.matterForecast.matterAutomation.error ?? '', 'error');
                }
            })
            // tslint:disable-next-line:no-any
            .catch((reason: any) => {
                showNotification('Failed to Update Matter Forecast', reason, 'error');
            });
    };
    
    const onExpectedDaysRemainingChange = (values: ForecastParametersFormValues, input: FieldInputProps<ForecastParametersFormValues, HTMLElement>, form: FormApi<ForecastParametersFormValues>) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value ? parseInt(event.target.value, 10) : null;
        input.onChange(value);
        
        // Calculate end date based on expected days remaining
        if (values.startDate && value !== null) {
            const endDate = moment(values.startDate).add(value, 'days').toDate();
            form.change('endDate', endDate);
        }
    };
    
    return (
        <Dialog 
            {...rest}
            fullWidth={true}
            maxWidth="md"
        >
            <Form<ForecastParametersFormValues>
                onSubmit={onSubmit}
                initialValues={initialValues}
                initialValuesEqual={() => true}
                // keepDirtyOnReinitialize={true}
                subscription={{ submitting: true, pristine: true, values: true }}
                render={({ handleSubmit, form, submitting, pristine, values}) => (
                    <form 
                        onSubmit={handleSubmit}
                        className={classes.root}
                    >
                        <DialogContent>
                            <Grid
                                container={true}
                                spacing={2}
                            >
                                <Grid
                                    item={true}
                                    xs={12}
                                >
                                <Box
                                        textAlign="center"
                                    >
                                        <Typography
                                            variant="h5"
                                            gutterBottom={true}
                                        >
                                            {props.title}
                          
                                        </Typography>          
                                        <Typography>
                                            {props.description}   
                                        </Typography>    
                                        {forecastAutomations?.type && (
                                            <Typography>
                                                Matter Type: {forecastAutomations.type}
                                            </Typography>
                                        )}
                                </Box>
                                </Grid>
                                <Grid
                                    item={true}
                                    xs={12}
                                >
                                    <Grid
                                        container={true}
                                        spacing={2}
                                    >
                                        <Grid
                                            item={true}
                                            md={6}
                                            xs={12}
                                        >
                                            <Grid
                                                container={true}
                                                spacing={1}
                                            >
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Paper
                                                        classes={{
                                                            root: classes.paper
                                                        }}
                                                        style={{
                                                            padding: '10px'
                                                        }}
                                                    >
                                                        <Grid
                                                            container={true}
                                                            spacing={1}
                                                        >
                                                            <Grid
                                                                item={true}
                                                                md={7}
                                                            >
                                                                Instruction Date
                                                            </Grid>
                                                            <Grid
                                                                item={true}
                                                                md={5}
                                                                style={{
                                                                    textAlign: 'right'
                                                                }}
                                                            >
                                                                {forecastAutomations?.instructionDate ? formatDate(forecastAutomations?.instructionDate, 'DD MMM YYYY') : ''}
                                                            </Grid>
                                                        </Grid>
                                                    </Paper>
                                                </Grid>
                                                
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Paper
                                                        classes={{
                                                            root: classes.paper
                                                        }}
                                                        style={{
                                                            padding: '10px'
                                                        }}
                                                    >
                                                        <Grid
                                                            container={true}
                                                            spacing={1}
                                                        >
                                                            <Grid
                                                                item={true}
                                                                md={7}
                                                            >
                                                                Average Duration for matter type
                                                            </Grid>
                                                            <Grid
                                                                item={true}
                                                                md={5}
                                                                style={{
                                                                    textAlign: 'right'
                                                                }}
                                                            >
                                                                {formatNumber(forecastAutomations?.averageDuration || 0)} Days
                                                            </Grid>
                                                        </Grid>
                                                    </Paper>
                                                </Grid>
                                                
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Field
                                                        name="expectedDaysRemaining"
                                                        validate={required}
                                                        subscription={{touched: true, error: true, value: true}}
                                                    >
                                                        {({ input, meta }) => (
                                                            <TextField
                                                                {...input}
                                                                label="Expected Days Remaining"
                                                                error={meta.error && meta.touched}
                                                                helperText={meta.error && meta.touched ? 'Required' : ''}
                                                                className={clsx(classes.textField, classes.rightInput)}
                                                                type="number"
                                                                onChange={onExpectedDaysRemainingChange(values, input, form)}
                                                                
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid>
                                                
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Field
                                                        name="startDate"
                                                        validate={required}
                                                        subscription={{touched: true, error: true, value: true}}
                                                    >
                                                        {({ input, meta }) => (
                                                            <KeyboardDatePicker
                                                                {...input}
                                                                label="Forecast Start"
                                                                format="MM/YYYY"
                                                                placeholder="mm/yyyy"
                                                                views={['year', 'month']}
                                                                // tslint:disable-next-line: max-line-length
                                                                animateYearScrolling={true}
                                                                // tslint:disable-next-line: max-line-length
                                                                autoOk={true}
                                                                allowKeyboardControl={false}
                                                                variant={'inline'}
                                                                error={meta.error && meta.touched}
                                                                helperText={meta.error && meta.touched ? 'Required' : ''}
                                                                inputProps={{autocomplete: 'off'}}
                                                                InputLabelProps={{ shrink: true}}
                                                                className={classes.textField}
                                                                onChange={(date: MaterialUiPickersDate, value?: string | null) => {
                                                                    input.onChange(date);
                                                                    if (!values.endDate) {
                                                                        
                                                                        const endDatePlusOne = date ? moment(date).add(1, 'months').toDate() : null;
                                                                        
                                                                        form.change('endDate', endDatePlusOne);
                                                                        
                                                                        const duration = getDurationType(date ? moment(date) : moment(), endDatePlusOne ? moment(endDatePlusOne) : moment());
                                                                        
                                                                        form.change('duration', duration);
                                                                    } else {
                                                                        const duration = getDurationType(date ? moment(date) : moment(), values.endDate ? moment(values.endDate) : moment());
                                                                        
                                                                        form.change('duration', duration);
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid>
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Field
                                                        name="endDate"
                                                        validate={required}
                                                        subscription={{touched: true, error: true, value: true}}
                                                    >
                                                        {({ input, meta }) => (
                                                            <KeyboardDatePicker
                                                                {...input}
                                                                label="Forecast End"
                                                                format="MM/YYYY"
                                                                placeholder="mm/yyyy"
                                                                views={['year', 'month']}
                                                                // tslint:disable-next-line: max-line-length
                                                                animateYearScrolling={true}
                                                                // tslint:disable-next-line: max-line-length
                                                                autoOk={true}
                                                                allowKeyboardControl={false}
                                                                variant={'inline'}
                                                                error={meta.error && meta.touched}
                                                                helperText={meta.error && meta.touched ? 'Required' : ''}
                                                                inputProps={{autocomplete: 'off'}}
                                                                InputLabelProps={{ shrink: true}}
                                                                className={classes.textField}
                                                                disabled={values.startDate ? false : true}
                                                                onChange={(date: MaterialUiPickersDate, value?: string | null) => {
                                                                    input.onChange(date);
                                                                    
                                                                    const duration = getDurationType(values.startDate ? moment(values.startDate) : moment(), date ? moment(date) : moment());
                                                                    
                                                                    form.change('duration', duration);
                                                                }}
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid>
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Typography
                                                        style={{
                                                            paddingTop: '10px'
                                                        }}
                                                    >
                                                        Forecast Duration: {values.duration}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            item={true}
                                            md={6}
                                            xs={12}
                                        >
                                            <Grid
                                                container={true}
                                                spacing={1}
                                            >
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Field
                                                        name="estimatedFees"
                                                        validate={required}
                                                        subscription={{ touched: true, error: true, value: true }}
                                                    >
                                                        {({ input, meta }) => {
                                                            const wrappedInput = {
                                                                ...input,
                                                                onChange: (value: number) => {
                                                                    input.onChange(value);
                                                                    // Calculations for feesToBeBilled
                                                                    const estimatedFees = value || 0;
                                                                    const averageFeesBilled = forecastAutomations?.averageFeesBilled || 0;
                                                                    const expectedTotalFees = estimatedFees > 0 ? estimatedFees : averageFeesBilled;
                                                                    
                                                                    const timeFeesBilledExTax = forecastAutomations?.timeFeesBilledExTax || 0;
                                                                    const remaining = expectedTotalFees - timeFeesBilledExTax;
                                                                    
                                                                    const feesToBeBilled = remaining <= 0 
                                                                        ? expectedTotalFees * 0.25 
                                                                        : remaining;
                                                                    
                                                                    form.change('feesToBeBilled', feesToBeBilled);
                                                                }
                                                            };
                                                            
                                                            return (
                                                                <CurrencyInput
                                                                    input={wrappedInput}
                                                                    meta={meta}
                                                                    label="Estimated Fees"
                                                                    prefix={currencyQuery.data ? currencyQuery.data.options.currencySymbol.symbol : '$'}
                                                                    className={classes.textField}
                                                                    helperText={meta.error && meta.touched ? 'Required' : ''}
                                                                />
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid>
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Paper
                                                        classes={{
                                                            root: classes.paper
                                                        }}
                                                        style={{
                                                            padding: '10px'
                                                        }}
                                                    >
                                                        <Grid
                                                            container={true}
                                                            spacing={1}
                                                        >
                                                            <Grid
                                                                item={true}
                                                                md={7}
                                                            >
                                                                Average Fees Billed for matter type
                                                            </Grid>
                                                            <Grid
                                                                item={true}
                                                                md={5}
                                                                style={{
                                                                    textAlign: 'right'
                                                                }}
                                                            >
                                                                {currencyQuery.data?.options.currencySymbol.symbol || '$'}{formatNumber(forecastAutomations?.averageFeesBilled || 0)}
                                                            </Grid>
                                                        </Grid>
                                                    </Paper>
                                                </Grid>
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Paper
                                                        classes={{
                                                            root: classes.paper
                                                        }}
                                                        style={{
                                                            padding: '10px'
                                                        }}
                                                    >
                                                        <Grid
                                                            container={true}
                                                            spacing={1}
                                                        >
                                                            <Grid
                                                                item={true}
                                                                md={7}
                                                            >
                                                                Fees Billed to Date
                                                            </Grid>
                                                            <Grid
                                                                item={true}
                                                                md={5}
                                                                style={{
                                                                    textAlign: 'right'
                                                                }}
                                                            >
                                                                {currencyQuery.data?.options.currencySymbol.symbol || '$'}{formatNumber(forecastAutomations?.feesBilledToDate || 0)}
                                                            </Grid>
                                                        </Grid>
                                                    </Paper>
                                                </Grid>
                                                
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Field
                                                        name="feesToBeBilled"
                                                        label="Forecast Fees To Be Billed"
                                                        subscription={{ touched: true, error: true, value: true }}
                                                        prefix={currencyQuery.data ? currencyQuery.data.options.currencySymbol.symbol : '$'}
                                                        className={classes.textField}
                                                        component={CurrencyInput}
                                                        validate={required}
                                                        style={{
                                                            textAlign: 'right'
                                                        }}
                                                    />
                                                </Grid>
                                                
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Field
                                                        name="debtorsDays"
                                                        label="Average Payment Days"
                                                        subscription={{ touched: true, error: true, value: true }}
                                                        className={classes.textField}
                                                        component={CurrencyInput}
                                                        validate={required}
                                                        prefix=""
                                                        decimalScale={0}
                                                    />
                                                </Grid>
                                                <Grid
                                                    item={true}
                                                    xs={12}
                                                >
                                                    <Field
                                                        name="chargeRate"
                                                        label="Average Charge Rate"
                                                        subscription={{ touched: true, error: true, value: true }}
                                                        prefix={currencyQuery.data ? currencyQuery.data.options.currencySymbol.symbol : '$'}
                                                        className={classes.textField}
                                                        component={CurrencyInput}
                                                        validate={required}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <div
                                className={classes.actionsWrapper}
                            >
                                 <Button
                                    type="submit"
                                    disabled={submitting}
                                    variant="outlined"
                                >
                                    Create Forecast
                                </Button>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={() => props.onClose?.({}, 'backdropClick')}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    );
}

interface MatterForecastAutomationDataVariables {
    offset?:    number;
    first?:     number;
    filter?:    string;
    matterId?:  number;
}

interface MatterForecastAutomationData {
    matterForecastAutomationList: MatterForecastAutomationList;
}

interface MatterForecastAutomationList {
    forecastAutomations: ForecastAutomations;
}

interface ForecastAutomations {
    matterId:               number;
    forecastStart:          Date;
    forecastEnd:            Date;
    expectedCompletionDate: Date;
    averageDuration:        number;
    forecastDuration:       null;
    debtorsDays:            number;
    estimatedFees:          number;
    averageFeesBilled:      number;
    feesBilledToDate:       number;
    timeFeesBilledExTax:    number;
    averageChargeRate:      number;
    forecastFeesToBeBilled: number;
    instructionDate:        Date;
    type:                  string;
}

const MatterForecastAutomationDataQuery = gql`
    query matterForecastAutomationData(
        $offset: Int
        $first: Int
        $filter: String
        $matterId: Int
    ) {
        matterForecastAutomationList(
            offset: $offset
            first: $first
            filter: $filter
            matterId: $matterId
        ) {
            forecastAutomations {
                matterId
                forecastStart
                forecastEnd
                expectedCompletionDate
                averageDuration
                forecastDuration
                debtorsDays
                estimatedFees
                averageFeesBilled
                feesBilledToDate
                timeFeesBilledExTax
                averageChargeRate
                forecastFeesToBeBilled
                instructionDate
                type
            }
        }
    }
`;

interface MatterForecastAutomationInput {
    matterGuid?:           string;
    forecastStartDate?:    Date;
    forecastEndDate?:      Date;
    feesBilled?:           number;
    debtorsDays?:          number;
    chargeRate?:           number;
}

interface UpdateMatterForecastMutationVariables {
    input: MatterForecastAutomationInput;
}

interface UpdateMatterForecastMutationData {
    matter: Matter;
}

interface Matter {
    matterForecast: MatterForecast;
}

interface MatterForecast {
    matterAutomation: MatterAutomation;
}

interface MatterAutomation {
    error:  string | null;
    status: boolean;
}


const UpdateMatterForecastMutation = gql`
    mutation updateMatterForecast($input: MatterForecastAutomationInput ) {
        matter {
            matterForecast {
            matterAutomation(input: $input) {
                error
                status
            }
            }
        }
    }
`;